import { Vue, Component, Watch } from "vue-property-decorator";
import { ValidationObserver } from "vee-validate";
import { IPrAffiliateGroupedByType, IRole, IUser, IUserGraph } from "@/models";
import { Modal, PeoplePicker } from "@/components";
import { Roles } from "@/enums";

@Component({
    components: {
        Modal,
        PeoplePicker,
    },
    metaInfo: {
        title: "My Campaigns | Pernod Ricard",
    },
})
export default class AdminUser extends Vue {
    public loading = false;

    public modal = false;

    public filterShowInactive = false;

    public searchPrAffiliate: string = null;

    public selectedPrAffiliate: any[] = [];

    public search = "";

    public filterRoleId = 0;

    public user: IUser = {
        userId: 0,
        email: "",
        name: "",
        userUniqueId: "",
        role: "",
        roleId: Roles.PrAffiliate,
        isActive: false,
        prAffiliatePermissions:[],
    };

    public userGraphs: IUserGraph[] = [];

    public headers = [
        { text: "Name", value: "name", width: "20%" },
        { text: "Email", value: "email", width: "45%" },
        { text: "Active", value: "isActive", width: "10%" },
        { text: "Role", value: "roleId", width: "15%", filterable: false },
        { text: "Actions", value: "actions", width: "10%", sortable: false },
    ];

    private allUsers: IUser[] = [];

    get isEditing(): boolean {
        return this.user.userId > 0;
    }

    get isUserFound(): boolean {
        return this.user.userUniqueId.length > 0;
    }

    get roles(): IRole[] {
        return this.$store.getters["app/roles"];
    }

    get users(): IUser[] {
        let users: IUser[] = this.$store.getters["app/users"];
        if (!this.filterShowInactive) {
            users = users.filter(u => u.isActive);
        }
        if (this.filterRoleId > 0) {
            users = users.filter(u => u.roleId === this.filterRoleId);
        }
        return users;
    }

    get showList(): boolean {
        return this.user.roleId === Roles.PrAffiliate;
    }

    get prAffiliates(): IPrAffiliateGroupedByType[] {
        const prAffiliatesTemp = this.$store.getters["app/prAffiliates"];
        if (prAffiliatesTemp !== null && prAffiliatesTemp.length > 0) {
            const prAffiliatesGroupedTypes: IPrAffiliateGroupedByType[] = [
                {
                    id:9997,
                    name:"Brand Co",
                    children: prAffiliatesTemp.filter(x => x.type === 1).map(item => ({id:item.prAffiliateId, name: item.name})),
                },
                {
                    id:9998,
                    name:"Market Co",
                    children: prAffiliatesTemp.filter(x => x.type === 2).map(item => ({id:item.prAffiliateId, name: item.name})),
                },
                {
                    id:9999,
                    name:"Other Team",
                    children: prAffiliatesTemp.filter(x => x.type === 3).map(item => ({id:item.prAffiliateId, name: item.name})),
                },
            ];
            return prAffiliatesGroupedTypes;
        }
        return [];
    }

    @Watch("userGraphs")
    onUserGraphsChanged(): void {
        if (this.userGraphs.length > 0) {
            const userGraph = this.userGraphs[0];
            if (this.allUsers.some(u => u.userUniqueId === userGraph.id)) {
                this.$emit("error", `The user '${ userGraph.displayName }' already exists`);
                this.resetUser();
            }
            else {
                this.$nextTick(() => {
                    this.user.userUniqueId = userGraph.id;
                    this.user.email = userGraph.mail;
                    this.user.name = userGraph.displayName;
                    this.user.roleId = 0;
                });
            }
        }
    }

    async mounted(): Promise<void> {
        this.loading = true;
        try {
            await this.$store.dispatch("app/loadUsers", true);
            await this.$store.dispatch("app/loadRoles", true);
            await this.$store.dispatch("app/loadAllPrAffiliate", true);
            this.allUsers = this.$store.getters["app/users"];
        }
        catch (error) {
            this.$emit("error", error);
        }
        this.loading = false;
    }

    createUser(): void {
        this.modal = true;
        this.selectedPrAffiliate = [];
        this.searchPrAffiliate = null;
    }

    editUser(user: IUser): void {
        this.user = Object.assign({}, user);
        this.modal = true;
        if (this.user?.prAffiliatePermissions.length > 0) {
            this.selectedPrAffiliate = this.user?.prAffiliatePermissions.map(item => ({id:item.prAffiliateId}));
            this.searchPrAffiliate = this.$store.getters["app/prAffiliates"].filter(x => x.prAffiliateId === this.selectedPrAffiliate[0].id).shift().name;
        }
        else {
            this.searchPrAffiliate = null;
            this.selectedPrAffiliate = [];
        }
    }

    getRoleName(roleId: number): string {
        const role = this.roles.find(r => r.roleId === roleId);
        return role ? role.name : "";
    }

    isPrAffiliateUser(user: IUser): boolean {
        return user.roleId === Roles.PrAffiliate;
    }

    onRoleChange(): void {
        if (!this.isPrAffiliateUser(this.user)) {
            this.user.prAffiliatePermissions = [];
        }
    }

    onSelected(e: IPrAffiliateGroupedByType[]): void {
        if (e.length > 0) {
            if (e[e.length -1].id > 9996) {
                this.selectedPrAffiliate = [];
            }
            else {
                this.selectedPrAffiliate = [e[e.length - 1]];
            }
        }
    }

    async submit(): Promise<void> {
        const action = this.isEditing ? "updateUser" : "addUser";
        const message = this.isEditing ? "edited" : "created";

        this.loading = true;
        try {
            this.user.prAffiliatePermissions = this.selectedPrAffiliate.map(item => ({prAffiliateId: Number(item.id)}));
            await this.$store.dispatch(`app/${action}`, this.user);
            this.$emit("success", `The User has been successfully ${message}.`);
            this.closeModal();
        }
        catch (error) {
            this.$emit("error", error);
        }
        this.loading = false;
    }

    closeModal(): void {
        this.modal = false;
        this.resetUser();
    }

    resetUser(): void {
        const observer = this.$refs.observer as InstanceType<typeof ValidationObserver>;

        this.user = {
            userId: 0,
            email: "",
            name: "",
            userUniqueId: "",
            roleId: Roles.PrAffiliate,
            role: "",
            isActive: false,
            prAffiliatePermissions: [],
        };

        this.userGraphs = [];

        if (observer) {
            observer.reset();
        }
    }
}
